import { KeyValuePipe } from '@angular/common'
import { Component, computed, input, linkedSignal, output } from '@angular/core'
import { FormField, disabled, form } from '@angular/forms/signals'

import { omit as _omit } from 'lodash'

import { FlxButtonModule, FlxFormControlModule, FlxModalModule } from '@flowx/angular-ui-toolkit'
import { parseColourValueOr } from '@flowx/core-theme'
import { FlxColourSwatchDirective } from '@flowx/designer/shared/directives/colour-swatch.directive'

import { ThemeColorShades } from '../../color.helpers'

/** Shades 0 and 1000 are fixed (transparent and black) and not editable. */
const FIXED_SHADES = ['0', '1000']

/** Edits every shade of one colour; changes are a local draft until Save. */
@Component({
  selector: 'flx-colors-shades-editor',
  imports: [
    KeyValuePipe,
    FormField,
    FlxModalModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxColourSwatchDirective,
  ],
  templateUrl: './colors-shades-editor.component.html',
  styleUrls: ['./colors-shades-editor.component.scss'],
  host: { class: 'max-h-full' },
})
export class FlxColorsShadesEditorComponent {
  /** What is typed in each shade field. */
  readonly #draft = linkedSignal<ThemeColorShades>(() => ({ ...this.shades() }))
  /** The last valid value of each shade, which Save emits. */
  readonly #committedShades = linkedSignal<ThemeColorShades>(() => ({ ...this.shades() }))

  readonly shades = input.required<ThemeColorShades>()
  readonly colorName = input<string>()
  readonly readOnly = input(false)
  readonly closeModal = output<void>()
  readonly saveShades = output<Partial<ThemeColorShades>>()

  readonly shadesForm = form(this.#draft, (path) => disabled(path, { when: () => this.readOnly() }))
  readonly displayShades = computed(() => _omit({ ...this.shades() }, FIXED_SHADES))

  /** Keeps a valid typed shade, normalised, and restores an invalid one. */
  commitShade(shade: string): void {
    const draftField = this.shadesForm[shade]
    const committedColour = parseColourValueOr(draftField().value(), this.#committedShades()[shade])
    // reset also rewrites the text field when the restored value is the one it showed last
    draftField().reset(committedColour)
    this.#committedShades.update((shades) => ({ ...shades, [shade]: committedColour }))
  }

  save(): void {
    Object.keys(this.displayShades())
      .filter((shade) => this.#draft()[shade] !== this.#committedShades()[shade])
      .forEach((shade) => this.commitShade(shade))
    this.saveShades.emit(this.#committedShades())
    this.closeModal.emit()
  }
}
